<template>
  <v-container>
    <h3>チャンネル基本情報設定</h3>

    <v-form @submit.prevent ref="form" class="mt-8 mb-5">

      <v-row>
        <v-col :cols="$vuetify.display.mobile ? 12 : 4" class="d-flex align-center">
          <v-label>チャンネル選択</v-label>
        </v-col>
        <v-col :cols="$vuetify.display.mobile ? 12 : 4">
          <v-select density="compact" hide-details="true"
            v-model:model-value="channelKey" :items="channels"></v-select>
        </v-col>
      </v-row>

      <v-divider class="my-7"></v-divider>

      <v-row v-if="channelKey !== ''">
        <v-col :cols="$vuetify.display.mobile ? 12 : 4" class="d-flex align-center">
          <v-label>チャンネル名称</v-label>
        </v-col>
        <v-col :cols="$vuetify.display.mobile ? 12 : 6">
          <v-text-field density="compact" counter
            :rules="[ v => (!!v) || '必須、20文字以内', v => (v && v.length <= 20) || '必須、20文字以内' ]"
            persistent-hint hint="必須、20文字以内"
            v-model="channelName" :readonly="!isConfigurable"></v-text-field>
        </v-col>

        <v-col :cols="$vuetify.display.mobile ? 12 : 4" class="d-flex align-center" v-if="channelKey.startsWith('a')">
          <v-label>計測値単位</v-label>
        </v-col>
        <v-col :cols="$vuetify.display.mobile ? 12 : 5" v-if="channelKey.startsWith('a')">
          <v-text-field density="compact" counter
            :rules="[ v => (!!v) || '必須、10文字以内', v => (v && v.length <= 10) || '必須、10文字以内' ]"
            persistent-hint hint="必須、10文字以内"
            v-model="channelUnit" :readonly="!isConfigurable"></v-text-field>
        </v-col>

        <v-col :cols="$vuetify.display.mobile ? 12 : 4" class="d-flex align-center" v-if="channelKey.startsWith('a')">
          <v-label>端子入力モード</v-label>
        </v-col>
        <v-col :cols="$vuetify.display.mobile ? 12 : 6" v-if="channelKey.startsWith('a')">
          <v-btn-toggle variant="outlined" divided class="mt-2 d-flex"
            mandatory v-model="channelMode">
            <v-btn color="primary" class="flex-fill" style="text-transform: none" value="mA"
              :disabled="!isConfigurable && channelMode === 'V'">
              4&nbsp;-&nbsp;20mA
            </v-btn>
            <v-btn color="primary" class="flex-fill" value="V"
              :disabled="!isConfigurable && channelMode === 'mA'">
              0&nbsp;-&nbsp;10V
            </v-btn>
          </v-btn-toggle>
          <p class="px-4 py-2 text-caption text-grey-darken-1">
            必須、モジュール本体の設定と一致させてください
          </p>
        </v-col>

        <v-col :cols="$vuetify.display.mobile ? 12 : 4" class="d-flex align-center" v-if="channelKey.startsWith('do')">
          <v-label>出力ONの場合の表示</v-label>
        </v-col>
        <v-col :cols="$vuetify.display.mobile ? 12 : 5" v-if="channelKey.startsWith('do')">
          <v-text-field density="compact" counter
            :rules="[ v => (!!v) || '必須、10文字以内', v => (v && v.length <= 10) || '必須、10文字以内' ]"
            persistent-hint hint="必須、10文字以内"
            v-model="highMeaning" :readonly="!isConfigurable"></v-text-field>
        </v-col>

        <v-col :cols="$vuetify.display.mobile ? 12 : 4" class="d-flex align-center" v-if="channelKey.startsWith('do')">
          <v-label>出力OFFの場合の表示</v-label>
        </v-col>
        <v-col :cols="$vuetify.display.mobile ? 12 : 5" v-if="channelKey.startsWith('do')">
          <v-text-field density="compact" counter
            :rules="[ v => (!!v) || '必須、10文字以内', v => (v && v.length <= 10) || '必須、10文字以内' ]"
            persistent-hint hint="必須、10文字以内"
            v-model="lowMeaning" :readonly="!isConfigurable"></v-text-field>
        </v-col>

        <v-col cols="12" class="my-2">
          <v-alert variant="tonal" type="success" v-model="isShowCompleteMessage" closable>
            変更しました
          </v-alert>
        </v-col>

        <v-col cols="12" class="mt-n2 mb-2" v-if="isShowResetUsageSettingMessage">
          <v-alert variant="tonal" type="info" v-model="isShowResetUsageSettingMessage" closable>
            端子入力モードを変更したため、使用設定がリセットされました
          </v-alert>
        </v-col>

        <v-col cols="12" class="mt-n2 mb-2" v-if="isShowResetAlarmThresholdsMessage">
          <v-alert variant="tonal" type="info" v-model="isShowResetAlarmThresholdsMessage" closable>
            端子入力モードを変更したため、アラームメール閾値設定がリセットされました
          </v-alert>
        </v-col>

        <v-col cols="12" class="text-end" v-if="isConfigurable">
          <v-btn :elevation="0" color="primary" :loading="isLoading" v-on:click="updateChannelInfo">
            変更する
          </v-btn>
        </v-col>
      </v-row>

    </v-form>
  </v-container>
</template>

<script>
import { defineComponent, ref, onMounted, watch } from 'vue';

import axios from 'axios';

import config from '@/config.js';

export default defineComponent({
  name: 'ChannelBasicInfoSetting',
  props: {
    moduleId: { type: String },
    isConfigurable: { type: Boolean }
  },

  setup(props) {

    const form = ref();

    const channelKey = ref('');
    const channels = ref([
      { title: '未選択', value: '' }
    ]);

    const channelInfoList = ref({});

    const channelName = ref('');

    const channelUnit = ref('');
    const channelMode = ref('mA');

    const highMeaning = ref('');
    const lowMeaning = ref('');

    const isLoading = ref(false);
    const isShowCompleteMessage = ref(false);

    const isShowResetUsageSettingMessage = ref(false);
    const isShowResetAlarmThresholdsMessage = ref(false);

    onMounted(() => {
      fetchChannelList();

      watch(channelKey, () => {

        isShowCompleteMessage.value = false;
        isShowResetUsageSettingMessage.value = false;
        isShowResetAlarmThresholdsMessage.value = false;

        if (channelKey.value === '') {
          return;
        }

        channelName.value = channelInfoList.value[channelKey.value].name;

        if (channelKey.value.startsWith('a')) {
          channelUnit.value = channelInfoList.value[channelKey.value].unit;
          channelMode.value = channelInfoList.value[channelKey.value].mode;
        }
        else if (channelKey.value.startsWith('do')) {
          highMeaning.value = channelInfoList.value[channelKey.value].highMeaning;
          lowMeaning.value = channelInfoList.value[channelKey.value].lowMeaning;
        }
        else {
          // do nothing
        }

      });
    });

    const fetchChannelList = () => {
      axios.get(
        config.API_URL +
        `/modules/${ props.moduleId }/channels`,
        { headers: { Authorization: 'Bearer ' + window.sessionStorage.getItem("access-token") } }
      ).then((response) => {

        for (let key in response.data.channels) {

          if (key.startsWith('a')) {
            channels.value.push({
              title: 'アナログ' + key.substring(1),
              value: key
            });
          }
          else if (key.startsWith('do')) {
            channels.value.push({
              title: 'デジタル出力' + key.substring(2),
              value: key
            });
          }
          else {
            channels.value.push({
              title: 'デジタル入力' + key.substring(1),
              value: key
            });
          }

          channelInfoList.value[key] = response.data.channels[key];

        }

      }).catch((/* error */) => {
        // TBD リフレッシュトークンの利用を検討
        window.sessionStorage.clear();
        window.sessionStorage.setItem('access-token-expired', 'true');

        window.location.reload();
      });
    };

    const updateChannelInfo = async () => {

      const { valid } = await form.value.validate();
      if (!valid) {
        return;
      }

      isLoading.value = true;
      isShowCompleteMessage.value = false;
      isShowResetUsageSettingMessage.value = false;
      isShowResetAlarmThresholdsMessage.value = false;

      const reqBody = {
        name: channelName.value
      };

      if (channelKey.value.startsWith('a')) {

        reqBody.unit = channelUnit.value;
        reqBody.mode = channelMode.value;

      }
      else if (channelKey.value.startsWith('do')) {

        reqBody.highMeaning = highMeaning.value;
        reqBody.lowMeaning = lowMeaning.value;

      }
      else {
        // do nothing
      }

      axios.patch(
        config.API_URL +
        `/modules/${ props.moduleId }/channels/${ channelKey.value }`,
        reqBody,
        { headers: { Authorization: 'Bearer ' + window.sessionStorage.getItem("access-token") } }
      ).then((response) => {

        channelInfoList.value[channelKey.value] = reqBody;

        isLoading.value = false;
        isShowCompleteMessage.value = true;

        isShowResetUsageSettingMessage.value = response.data.isResetUsageSettingDone;
        isShowResetAlarmThresholdsMessage.value = response.data.isResetAlarmThresholdsDone;

      }).catch((/* error */) => {
        // TBD リフレッシュトークンの利用を検討
        window.sessionStorage.clear();
        window.sessionStorage.setItem('access-token-expired', 'true');

        window.location.reload();
      });

    };

    return {
      form,

      channelKey,
      channels,

      channelName,

      channelUnit,
      channelMode,

      highMeaning,
      lowMeaning,

      isLoading,
      isShowCompleteMessage,

      isShowResetUsageSettingMessage,
      isShowResetAlarmThresholdsMessage,

      updateChannelInfo
    }

  }
});
</script>
